import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import useDesktopChecker from "hooks/useDesktopChecker";
import chevronDown from "assets/images/v2/chevron-down.svg";
import { LoadingPageProps } from "pages/dashboard/v2/loadingPageProps";
import TogglePills from "components/v2/toggle-pills/TogglePills";
import { Spinner } from "components/v2/spinner/spinner";
import {
  LOAN_DETAILS_CARD_APR_LABEL,
  LOAN_DETAILS_CARD_ASSOCIATION_DUES_LABEL,
  LOAN_DETAILS_CARD_DOWN_PAYMENT_LABEL,
  LOAN_DETAILS_CARD_ESTIMATED_CLOSING_DATE_LABEL,
  LOAN_DETAILS_CARD_ESTIMATED_TOT_MON_PAYMENT_LABEL,
  LOAN_DETAILS_CARD_INSURANCE_LABEL,
  LOAN_DETAILS_CARD_INTEREST_RATE_LABEL,
  LOAN_DETAILS_CARD_INTEREST_RATE_LOCK_STATUS_LABEL,
  LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
  LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
  LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
  LOAN_DETAILS_CARD_LOAN_TERM_LABEL,
  LOAN_DETAILS_CARD_LOAN_TYPE_LABEL,
  LOAN_DETAILS_CARD_MORTGAGE_INSURANCE_LABEL,
  LOAN_DETAILS_CARD_OTHER_LABEL,
  LOAN_DETAILS_CARD_PRINCIPAL_INTEREST_LABEL,
  LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
  LOAN_DETAILS_CARD_PROPERTY_APPRAISED_VALUE_LABEL,
  LOAN_DETAILS_CARD_PROPERTY_TAX_LABEL,
  LOAN_DETAILS_CARD_PROPERTY_USE_LABEL,
  LOAN_DETAILS_CARD_PURCHASE_PRICE_LABEL,
  LOAN_DETAILS_CARD_RATE_AMORTIZATION_TYPE_LABEL,
  LOAN_DETAILS_CARD_RATE_LOCK_EXPIRATION_LABEL,
  LOAN_DETAILS_CARD_SUPPLEMENTAL_PROPERTY_INSURANCE_LABEL,
  LoanPurposeNames,
  NOT_AVAILABLE,
} from "utilities/Constant";
import docErrorImg from "assets/images/v2/error-illustration.svg";
import styles from "./LoanDetails.module.scss";
import { useSelector } from "react-redux";
import { getLoanPurpose, getLoanId, getNhDealApplicationId } from "features/authSlice";
import { PropertyAddress, PropertyLocation, useGetLoanSummaryDetailQuery, useGetEncompassLoanDataQuery } from "pages/dashboard/home/dashboardService";
import { formatDollarAmount } from "utilities/string";
import { skipToken } from '@reduxjs/toolkit/query';

interface LoanDetailsPageProps extends LoadingPageProps {
  readonly isLoanDetailsLoading?: boolean;
  readonly loanDetailsLoadError?: boolean;
}

const loanDetailTabs = Object.freeze([
  { label: "Overview", value: "overview" },
  { label: "Terms", value: "terms" },
  { label: "Dates", value: "dates" },
] as const);
type TabName = (typeof loanDetailTabs)[number]["value"];

interface KeyValueProps {
  id: string;
  label: string;
  value?: string | number;
  updatedValue?: boolean;
}

const KeyValue: React.FC<KeyValueProps> = memo(
  ({ id, label, value, updatedValue = false }) => (
    <div className={styles.keyValuePair} data-testid={`${id}-section`}>
      <div className={updatedValue ? styles.keyValueInnerUpdated : styles.keyValueInner}>
        <div className={styles.keyValuePairWrapper}>
          <span
            className={`${updatedValue ? styles.updated + ' label-medium-medium updated' : styles.keyLabel + ' label-medium-regular'}`}
            id={`${id}-label`}
            data-testid={`${id}-label`}
          >
            {label}
          </span>
          {updatedValue && <span className={styles.updatedBadge}>Updated</span>}
        </div>

        <div className={`${styles.keyValuePairWrapper} label-large-medium`} data-testid={`${id}-value`} aria-live="polite">
          <span className={styles.keyValue} aria-labelledby={`${id}-label`}>
            {value ?? NOT_AVAILABLE}
          </span>
          {label === LOAN_DETAILS_CARD_RATE_LOCK_EXPIRATION_LABEL && <span className={styles.updatedBadge}>Updated</span>}
        </div>
      </div>
    </div>
  )
);
KeyValue.displayName = "KeyValue";

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="container">
    <div className="wrapper">
      <div className="content">{children}</div>
    </div>
  </div>
);

const getLoanSummaryFieldFromPropertyId = (field: string): string => {
  if (field.startsWith("purpose-")) return "LoanPurpose";
  if (field.startsWith("number-")) return "LoanNumber";
  if (field.startsWith("amount-")) return "LoanAmount";
  if (field.startsWith("price-")) return "PurchasePrice";
  if (field.startsWith("down-payment-")) return "TotalDownPaymentAmount";
  if (field.startsWith("address-")) return "PropertyLocation";
  return "";
}

const isLoanPropertyUpdated = (propertyId: string, updatedFields?: string[]): boolean => {
  if (!updatedFields) return false;

  const fieldName = getLoanSummaryFieldFromPropertyId(propertyId);
  return updatedFields.includes(fieldName);
}

const LoanDetails: React.FC<LoanDetailsPageProps> = ({
  onLoad,
  isLoanDetailsLoading,
  loanDetailsLoadError,
}) => {
  const loanPurpose = useSelector(getLoanPurpose);
  const isRefinance = useMemo(
    () =>
      loanPurpose?.toLowerCase() ===
      LoanPurposeNames.Refinance.toLowerCase(),
    [loanPurpose]
  );

  useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  const uid = useId().replaceAll(":", "");
  const [selectedTab, setSelectedTab] = useState<TabName>("overview");
  const [isHighlightedContentsExpanded, setIsHighlightedContentsExpanded] =
    useState(false);
  const isDesktop = useDesktopChecker();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanId = useSelector(getLoanId);

  const {
    data: loanSummary,
    isLoading: isLoanSummaryLoading,
    isError: isLoanSummaryError,
    refetch: refetchLoanSummary
  } = useGetLoanSummaryDetailQuery(
    nhDealApplicationId && loanId
      ? { nhDealApplicationId, loanId, persistState: true }
      : skipToken
  );

  const hasRefetchedRef = useRef(false);

  useEffect(() => {
  if (
      loanSummary &&
    loanSummary.purchasePrice === null &&
    !hasRefetchedRef.current
  ) {
    hasRefetchedRef.current = true;
    void refetchLoanSummary();
  }
}, [loanSummary, refetchLoanSummary]);

  const loanNumber = loanSummary?.loanNumber;

  const { data: encompassLoanData, isLoading: isEncompassLoanDataLoading, isError: isEncompassLoanDataError } =
    useGetEncompassLoanDataQuery(
      nhDealApplicationId && loanNumber && loanNumber !== "TBD"
        ? { nhDealApplicationId }
        : skipToken
    );

  const toggleHighlightedContentsExpand = useCallback(() => {
    setIsHighlightedContentsExpanded((prev) => !prev);
  }, []);

  // Format property location using the specified address fields
  const formatPropertyLocation = (propertyLocation?: PropertyLocation): string => {
    if (!propertyLocation) return NOT_AVAILABLE;

    const { addressLineText, addressUnitType, addressUnitIdentifier, cityName, stateCode, postalCode } = propertyLocation;

    const isAddressEmpty = (value?: string) => !value || ["", "tbd", "tba"].includes(value.toLowerCase());
    const isFieldEmpty = (value?: string) => !value || value.trim() === "";

    if (isAddressEmpty(addressLineText) && isFieldEmpty(addressUnitType) && isFieldEmpty(addressUnitIdentifier) && isFieldEmpty(cityName) && isFieldEmpty(stateCode) && isFieldEmpty(postalCode)) {
      return NOT_AVAILABLE;
    }

    const parts: string[] = [];

    let addressLine = isAddressEmpty(addressLineText) ? NOT_AVAILABLE : addressLineText!;

    const unitParts: string[] = [];
    if (!isFieldEmpty(addressUnitType)) {
      unitParts.push(addressUnitType!);
    }
    if (!isFieldEmpty(addressUnitIdentifier)) {
      unitParts.push(addressUnitIdentifier!);
    }
    if (unitParts.length > 0) {
      addressLine += ` ${unitParts.join(" ")}`;
    }

    parts.push(addressLine);

    if (!isFieldEmpty(cityName)) {
      parts.push(cityName!);
    }

    if (!isFieldEmpty(stateCode) && !isFieldEmpty(postalCode)) {
      parts.push(`${stateCode!} ${postalCode!}`);
    } else if (!isFieldEmpty(stateCode)) {
      parts.push(stateCode!);
    } else if (!isFieldEmpty(postalCode)) {
      parts.push(postalCode!);
    }

    return parts.join(", ");
  };

  const formatPropertyAddress = (propertyAddress?: PropertyAddress): string => {
    if (!propertyAddress) return NOT_AVAILABLE;

    const { streetAddress, addressUnitDesignatorType, addressUnitIdentifier, city, state, postalCode } = propertyAddress;

    const isFieldEmpty = (value?: string | null) => !value || value.trim() === "";

    if (isFieldEmpty(streetAddress) && isFieldEmpty(addressUnitDesignatorType) && isFieldEmpty(addressUnitIdentifier) && isFieldEmpty(city) && isFieldEmpty(state) && isFieldEmpty(postalCode)) {
      return NOT_AVAILABLE;
    }

    const parts: string[] = [];
    let addressLine = isFieldEmpty(streetAddress) ? NOT_AVAILABLE : streetAddress!;

    const unitParts: string[] = [];
    if (!isFieldEmpty(addressUnitDesignatorType)) {
      unitParts.push(addressUnitDesignatorType!);
    }
    if (!isFieldEmpty(addressUnitIdentifier)) {
      unitParts.push(addressUnitIdentifier!);
    }
    if (unitParts.length > 0) {
      addressLine += ` ${unitParts.join(" ")}`;
    }

    parts.push(addressLine);

    if (!isFieldEmpty(city)) {
      parts.push(city!);
    }

    if (!isFieldEmpty(state) && !isFieldEmpty(postalCode)) {
      parts.push(`${state!} ${postalCode!}`);
    } else if (!isFieldEmpty(state)) {
      parts.push(state!);
    } else if (!isFieldEmpty(postalCode)) {
      parts.push(postalCode!);
    }

    return parts.join(", ");
  };

  // Format currency values
  const formatCurrency = (value?: number | null): string => {
    if (value === null || value === undefined) return NOT_AVAILABLE;
    return formatDollarAmount(value.toString()) ?? NOT_AVAILABLE;
  };

  // Format percentage values
  const formatPercentage = (value?: number | null): string => {
    if (value === null || value === undefined) return NOT_AVAILABLE;
    return `${value}%`;
  };

  // Format date values
  const formatDate = (dateString?: string): string => {
    if (!dateString) return NOT_AVAILABLE;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return NOT_AVAILABLE;
    }
  };

  // Format loan term
  const formatLoanTerm = (termInMonths?: number): string => {
    if (!termInMonths) return NOT_AVAILABLE;
    const years = termInMonths / 12;
    return years % 1 === 0 ? `${years} Years` : `${termInMonths} Months`;
  };


  // Format rate lock status
  const formatRateLockStatus = (rateLock?: { buySideExpirationDate?: string | null; lockValidationStatus?: string | null }): string => {
    if (!rateLock) return NOT_AVAILABLE;

    if (rateLock.buySideExpirationDate) {
      return "Locked";
    }

    if (rateLock.lockValidationStatus) {
      return rateLock.lockValidationStatus;
    }

    return "Not Locked";
  };

  // Build overview contents from loan summary data
  // If any field is missing/null/empty, display "Not Available"
  // Desktop order for overview
  const overviewContents = useMemo(
    () => {
      const propertyAddressFormatted = formatPropertyAddress(encompassLoanData?.property);

      return [
        {
          id: `purpose-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
          value: encompassLoanData?.loanPurpose || loanSummary?.loanPurpose || NOT_AVAILABLE
        },
        {
          id: `number-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
          value: encompassLoanData?.loanNumber || loanSummary?.loanNumber || NOT_AVAILABLE
        },
        {
          id: `amount-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
          value: formatCurrency(encompassLoanData?.borrowerRequestedLoanAmount ?? loanSummary?.loanAmount ?? null)
        },
        {
          id: `price-${uid}`,
          label: LOAN_DETAILS_CARD_PURCHASE_PRICE_LABEL,
          value: formatCurrency(encompassLoanData?.purchasePriceAmount ?? loanSummary?.purchasePrice ?? null)
        },
        {
          id: `down-payment-${uid}`,
          label: LOAN_DETAILS_CARD_DOWN_PAYMENT_LABEL,
          value: encompassLoanData?.downPaymentPercent ?
            formatPercentage(encompassLoanData.downPaymentPercent) :
            formatCurrency(loanSummary?.totalDownPaymentAmount ?? null)
        },
        {
          id: `address-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
          value: propertyAddressFormatted === NOT_AVAILABLE ?
            formatPropertyLocation(loanSummary?.propertyLocation) :
            propertyAddressFormatted
        }
      ];
    },
    [uid, encompassLoanData, loanSummary]
  );

  // Mobile order for overview
  const overviewMobileContents = useMemo(
    () => {
      const propertyAddressFormatted = formatPropertyAddress(encompassLoanData?.property);

      return [
        {
          id: `purpose-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
          value: encompassLoanData?.loanPurpose || loanSummary?.loanPurpose || NOT_AVAILABLE
        },
        {
          id: `number-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
          value: encompassLoanData?.loanNumber || loanSummary?.loanNumber || NOT_AVAILABLE
        },
        {
          id: `amount-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
          value: formatCurrency(encompassLoanData?.borrowerRequestedLoanAmount ?? loanSummary?.loanAmount ?? null)
        },
        {
          id: `address-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
          value: propertyAddressFormatted === NOT_AVAILABLE ?
            formatPropertyLocation(loanSummary?.propertyLocation) :
            propertyAddressFormatted
        },
        {
          id: `price-${uid}`,
          label: LOAN_DETAILS_CARD_PURCHASE_PRICE_LABEL,
          value: formatCurrency(encompassLoanData?.purchasePriceAmount ?? loanSummary?.purchasePrice ?? null)
        },
        {
          id: `down-payment-${uid}`,
          label: LOAN_DETAILS_CARD_DOWN_PAYMENT_LABEL,
          value: encompassLoanData?.downPaymentPercent ?
            formatPercentage(encompassLoanData.downPaymentPercent) :
            formatCurrency(loanSummary?.totalDownPaymentAmount ?? null)
        }
      ];
    },
    [uid, encompassLoanData, loanSummary]
  );

  // Desktop order for refinance
  const overviewRefinanceContents = useMemo(
    () => {
      const propertyAddressFormatted = formatPropertyAddress(encompassLoanData?.property);

      return [
        {
          id: `purpose-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
          value: encompassLoanData?.loanPurpose || loanSummary?.loanPurpose || NOT_AVAILABLE
        },
        {
          id: `number-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
          value: encompassLoanData?.loanNumber || loanSummary?.loanNumber || NOT_AVAILABLE
        },
        {
          id: `amount-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
          value: formatCurrency(encompassLoanData?.borrowerRequestedLoanAmount ?? loanSummary?.loanAmount ?? null)
        },
        {
          id: `appraised-value-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_APPRAISED_VALUE_LABEL,
          value: formatCurrency(encompassLoanData?.propertyAppraisedValueAmount ?? null)
        },
        {
          id: `address-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
          value: propertyAddressFormatted === NOT_AVAILABLE ?
            formatPropertyLocation(loanSummary?.propertyLocation) :
            propertyAddressFormatted
        }
      ];
    },
    [uid, encompassLoanData, loanSummary]
  );

  // Mobile order for refinance
  const overviewRefinanceMobileContents = useMemo(
    () => {
      const propertyAddressFormatted = formatPropertyAddress(encompassLoanData?.property);

      return [
        {
          id: `purpose-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
          value: encompassLoanData?.loanPurpose || loanSummary?.loanPurpose || NOT_AVAILABLE
        },
        {
          id: `number-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
          value: encompassLoanData?.loanNumber || loanSummary?.loanNumber || NOT_AVAILABLE
        },
        {
          id: `amount-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
          value: formatCurrency(encompassLoanData?.borrowerRequestedLoanAmount ?? loanSummary?.loanAmount ?? null)
        },
        {
          id: `address-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
          value: propertyAddressFormatted === NOT_AVAILABLE ?
            formatPropertyLocation(loanSummary?.propertyLocation) :
            propertyAddressFormatted
        },
        {
          id: `appraised-value-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_APPRAISED_VALUE_LABEL,
          value: formatCurrency(encompassLoanData?.propertyAppraisedValueAmount ?? null)
        }
      ];
    },
    [uid, encompassLoanData, loanSummary]
  );

  const termContents = useMemo(
    () => [
      {
        id: `term-${uid}`,
        label: LOAN_DETAILS_CARD_LOAN_TERM_LABEL,
        value: formatLoanTerm(encompassLoanData?.loanAmortizationTermMonths)
      },
      {
        id: `type-${uid}`,
        label: LOAN_DETAILS_CARD_LOAN_TYPE_LABEL,
        value: encompassLoanData?.mortgageType ?? NOT_AVAILABLE
      },
      {
        id: `property-use-${uid}`,
        label: LOAN_DETAILS_CARD_PROPERTY_USE_LABEL,
        value: encompassLoanData?.occupancyType ?? NOT_AVAILABLE
      },
      {
        id: `interest-rate-${uid}`,
        label: LOAN_DETAILS_CARD_INTEREST_RATE_LABEL,
        value: formatPercentage(encompassLoanData?.initialInterestRate)
      },
      {
        id: `apr-${uid}`,
        label: LOAN_DETAILS_CARD_APR_LABEL,
        value: encompassLoanData?.actualMfiHundredPercent ? formatPercentage(encompassLoanData.actualMfiHundredPercent / 100) : NOT_AVAILABLE
      },
      {
        id: `lock-status-${uid}`,
        label: LOAN_DETAILS_CARD_INTEREST_RATE_LOCK_STATUS_LABEL,
        value: formatRateLockStatus(encompassLoanData?.rateLock)
      },
      {
        id: `amortization-typ-${uid}`,
        label: LOAN_DETAILS_CARD_RATE_AMORTIZATION_TYPE_LABEL,
        value: encompassLoanData?.loanAmortizationType || NOT_AVAILABLE
      }
    ],
    [uid, encompassLoanData]
  );

  const termHighlightedContents = useMemo(
    () => [
      {
        id: `prin-ins-${uid}`,
        label: LOAN_DETAILS_CARD_PRINCIPAL_INTEREST_LABEL,
        value: formatCurrency(encompassLoanData?.principalAndInterestMonthlyPaymentAmount),
        badge: true
      },
      {
        id: `prop-tax-${uid}`,
        label: LOAN_DETAILS_CARD_PROPERTY_TAX_LABEL,
        value: formatCurrency(Number(encompassLoanData?.proposedRealEstateTaxesAmount))
      },
      {
        id: `ins-${uid}`,
        label: LOAN_DETAILS_CARD_INSURANCE_LABEL,
        value: formatCurrency(Number(encompassLoanData?.proposedHazardInsuranceAmount))
      },
      {
        id: `asso-dues-${uid}`,
        label: LOAN_DETAILS_CARD_ASSOCIATION_DUES_LABEL,
        value: formatCurrency((encompassLoanData?.proposedDuesAmount))
      },
      {
        id: `other-${uid}`,
        label: LOAN_DETAILS_CARD_OTHER_LABEL,
        value: formatCurrency((encompassLoanData?.proposedOtherAmount))
      },
      {
        id: `supp-prop-ins-${uid}`,
        label: LOAN_DETAILS_CARD_SUPPLEMENTAL_PROPERTY_INSURANCE_LABEL,
        value: formatCurrency((encompassLoanData?.supplementalPropertyInsuranceAmount))
      },
      {
        id: `mort-ins-${uid}`,
        label: LOAN_DETAILS_CARD_MORTGAGE_INSURANCE_LABEL,
        value: NOT_AVAILABLE
      },
    ],
    [uid, encompassLoanData]
  );

  const dateContents = useMemo(
    () => [
      {
        id: `close-date-${uid}`,
        label: LOAN_DETAILS_CARD_ESTIMATED_CLOSING_DATE_LABEL,
        value: formatDate(encompassLoanData?.closingDocument?.actualRescindedDate)
      },
      {
        id: `lock-exp-${uid}`,
        label: LOAN_DETAILS_CARD_RATE_LOCK_EXPIRATION_LABEL,
        value: formatDate(encompassLoanData?.rateLock?.buySideExpirationDate)
      }
    ],
    [uid, encompassLoanData]
  );

  // Extract nested ternary operation and use positive condition
  let overviewContent: typeof overviewContents;

  if (isRefinance) {
    overviewContent = isDesktop ? overviewRefinanceContents : overviewRefinanceMobileContents;
  } else {
    overviewContent = isDesktop ? overviewContents : overviewMobileContents;
  }

  const loanDetailTabContents: Record<TabName, typeof overviewContents> = {
    overview: overviewContent,
    terms: termContents,
    dates: dateContents,
  };

  const selectedTabContents = loanDetailTabContents[selectedTab];

  // Combined loading state
  const isLoading = isLoanDetailsLoading || isLoanSummaryLoading || isEncompassLoanDataLoading;

  // Combined error state
  const hasError = loanDetailsLoadError || (isLoanSummaryError && isEncompassLoanDataError);

  // ---- Spinner
  if (isLoading) {
    return (
      <PageShell>
        <div className={`${styles.spinner} ${styles.loadingContainer}`}>
          <Spinner isApi />
          <div className={styles.loadingText}>
            <div className={styles.loadingTitle}>Loading Loan Details</div>
            <div className={styles.loadingSubtitle}>
              This will take a moment!
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ---- Error
  if (hasError) {
    return (
      <PageShell>
        <div className={styles.previewError}>
          <img
            src={docErrorImg}
            alt="Error loading loan details"
            className={styles.errorImage}
          />
          <div className={styles.errorText}>
            <div className={styles.errorTitle}>Something went wrong</div>
            <div>
              We are having issues loading this page, Please try again later.
            </div>
            <div>We apologize for the inconvenience!</div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ---- Loan Details
  return (
    <div className={styles.content}>
      <div className={styles.loanDetails}>Loan Details</div>
      <div className={styles.hereCurrentLoanDetails}>
        Here are your current loan application details. This information is for
        reference only and is subject to change.
      </div>

      <TogglePills
        options={loanDetailTabs}
        value={selectedTab}
        onChange={setSelectedTab}
      />

      <div className={styles.loanDetailCard}>
        <div className={styles.cardBody}>
          <div className={styles.keyValueSectionWrapper}>
            {selectedTabContents.map((content) => (
              <KeyValue
                key={content.id}
                id={content.id}
                label={content.label}
                value={content.value}
                updatedValue={isLoanPropertyUpdated(content.id, loanSummary?.updatedFields)}
              />
            ))}
          </div>
        </div>

        {/* Highlighted section - ONLY for Terms tab */}
        {selectedTab === "terms" && (
          <div className={styles.highlightSection}>
            <div
              className={styles.highlightHeader}
              onClick={toggleHighlightedContentsExpand}
              role="button"
              aria-expanded={isHighlightedContentsExpanded}
            >
              <div className={styles.labelRow}>
                {LOAN_DETAILS_CARD_ESTIMATED_TOT_MON_PAYMENT_LABEL}
              </div>
              <div className={styles.valueRow}>
                <div className={styles.highlightKeyValue}>
                  {formatCurrency(
                    ((Number(encompassLoanData?.principalAndInterestMonthlyPaymentAmount) || 0) +
                      (Number(encompassLoanData?.proposedRealEstateTaxesAmount) || 0) +
                      (Number(encompassLoanData?.proposedHazardInsuranceAmount) || 0) +
                      (Number(encompassLoanData?.proposedDuesAmount) || 0) +
                      (Number(encompassLoanData?.proposedOtherAmount) || 0) +
                      (Number(encompassLoanData?.supplementalPropertyInsuranceAmount) || 0)) || null
                  )}
                </div>
                <span className={styles.updatedBadge}>Updated</span>
                <img
                  className={`${styles.chevron} ${isHighlightedContentsExpanded ? styles.expanded : ""
                    }`}
                  src={chevronDown}
                  alt={isHighlightedContentsExpanded ? "Collapse" : "Expand"}
                />
              </div>
            </div>
            <div
              className={`${styles.highlightDivider} ${isHighlightedContentsExpanded ? styles.showDivider : ""
                }`}
            />
            <div
              className={`${styles.collapsibleBody} ${isHighlightedContentsExpanded ? styles.expanded : ""
                }`}
            >
              <div className={styles.highlightBody}>
                <div className={styles.keyValueSectionWrapper}>
                  {termHighlightedContents.map((content) => (
                    <KeyValue
                      key={content.id}
                      id={content.id}
                      label={content.label}
                      value={content.value}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanDetails;